import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Me } from 'src/providers/me/me';
import { Pros } from 'src/providers/pros/pros';
import { Communication } from 'src/providers/communication/communication';

import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';
import { EnableGuestSmartNumberOnboarding } from '../enable-guest-smart-number-onboarding/enable-guest-smart-number-onboarding';

@Component({
  selector: 'notify-pros-of-change-onboarding',
  templateUrl: './notify-pros-of-change-onboarding.html',
  styleUrls: ['./notify-pros-of-change-onboarding.scss'],
  standalone: false
})
export class NotifyProsOfChangeOnboarding implements OnInit {

  allProItems: any[] = [];
  selectedPros: any[] = [];
  didCopyMessage = false;
  loaded = false;
  form: FormGroup;
  generatedMessage = '';
  messageCopied = false;
  userName = '[YOUR NAME]';
  businessName = '[YOUR BUSINESS]';
  sendingInProgress = false;
  prosSendStatus: { pro: any; status: 'pending' | 'sending' | 'success' | 'error'; error?: string }[] = [];
  howToSendItems = [
    {value: 'sms', viewValue: 'Automatically - send SMS to my pros for me'},
    {value: 'copy', viewValue: 'Manually - I\'ll copy and paste the message'}
  ];
  didCompleteProOnboarding = false;
  didCompleteSmartNumberOnboarding = false;
  
  
  boundGoToMyProsPage = this.goToMyProsPage.bind(this);
  boundGoToSmartNumberPage = this.goToSmartNumberPage.bind(this);
  boundSendSMSToPros = this.sendSMSToPros.bind(this);
  boundMarkStepComplete = this.markStepComplete.bind(this);

constructor(
    public onboardingProvider: OnboardingProvider,
    private util: Util,
    private formBuilder: FormBuilder,
    private me: Me,
    private pros: Pros,
    private communication: Communication,
    private storage: TidyStorage,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService
  ) {
    this.form = this.formBuilder.group({
      useTodos: [true],
      useInvoicing: [false],
      useTimeTracking: [false],
      sendingRequests: [true],
      messageText: [''],
      howToSend: ['sms']
    });
  }

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Notify Pros');
    this.loaded = false;
    try {
      const meData = await this.me.load();
      await this.checkIfStepsComplete();
      if (this.didCompleteProOnboarding) {
        const allPros = await this.pros.getAllPrivatePros();
        this.allProItems = allPros.map(pro => ({
          value: pro.id,
          viewValue: `${pro.first_name  } ${  pro.last_name}`
        }));
        this.selectedPros = this.allProItems;
      }
      this.userName = meData.current_user?.name || '[YOUR NAME]';
      this.businessName = meData.customer_account?.account_name || meData.customer_account?.name || '[YOUR BUSINESS]';
      const hasTodos = await this.onboardingProvider.checkIfDidFinishToDosOnboarding();
      if (hasTodos) {
        this.form.patchValue({ useTodos: true });
      }
      this.form.patchValue({ howToSend: 'sms' });
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
    this.generateMessage();
    this.loaded = true;
  }

  async checkIfStepsComplete() {
    this.didCompleteProOnboarding = false;
    this.didCompleteSmartNumberOnboarding = false;
    const onboardingGoals = await this.storage.retrieve('onboardingGoals');
    onboardingGoals.map((goal) => {
      goal.user_goal_steps.map((step) => {
        if (step.flow === 'addPros' && step.state === 'done') {
          this.didCompleteProOnboarding = true;
        }
        if (step.flow === 'enableGuestSmartNumber' && step.state === 'done') {
          this.didCompleteSmartNumberOnboarding = true;
        }
      })
    })
  }

  updateSelectedPros(pro) {
    if (this.selectedPros.includes(pro)) {
      this.selectedPros = this.selectedPros.filter(p => p !== pro);
    } else {
      this.selectedPros.push(pro);
    }
  }

  goToMyProsPage() {
    this.rightSidePanelService.closeRightSidePanel();
    this.navCtrl.navigateRoot('contacts');
  }

  goToSmartNumberPage() {
    this.storage.save('dialog-params', { redirectAfterSmartNumber: 'notifyProsOfChange' });
    this.rightSidePanelService.setDialogPageTitle('Smart Number');
    this.rightSidePanelService.navigateTo('onboarding/enableGuestSmartNumber', {}, EnableGuestSmartNumberOnboarding);
  }

  generateMessage() {
    const name = this.userName;
    const business = this.businessName;
    const message = `Hi, it's ${name} from ${business}.

📱 This is my new number - please use this for all communication.

I'm using TIDY to help me manage my property. They will chat with you on this line too.

Don't worry, it should mean very little changes for you:
✓ Pay stays the same
✓ No app required (they have one if you like)

Next steps:
📆 Keep doing any scheduled jobs.
👍 Accept new job requests that come in.

I appreciate you and am excited to keep working together.`;
    this.generatedMessage = message;
    this.form.patchValue({ messageText: message });
  }

  copyMessage() {
    this.didCopyMessage = true;
    const messageToSend = this.form.get('messageText')?.value || this.generatedMessage;
    const textArea = document.createElement('textarea');
    textArea.value = messageToSend;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.messageCopied = true;
    setTimeout(() => this.messageCopied = false, 3000);
  }

  async sendSMSToPros() {
    try {
      this.sendingInProgress = true;
      this.prosSendStatus = this.selectedPros.map(pro => ({
        pro,
        status: 'pending' as const
      }));
      for (let i = 0; i < this.prosSendStatus.length; i++) {
        const item = this.prosSendStatus[i];
        item.status = 'sending';
        try {
          const messageToSend = this.form.get('messageText')?.value || this.generatedMessage;
          const payload = {
            message: {
              text: messageToSend,
              files: []
            },
            to_homekeeper_id: item.pro.value
          };
          await this.communication.sendSharedInboxMessage(payload);
          item.status = 'success';
        } catch (error) {
          item.status = 'error';
          item.error = error?.error?.message || 'Failed to send message';
        }
        if (i < this.prosSendStatus.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      const successCount = this.prosSendStatus.filter(item => item.status === 'success').length;
      const errorCount = this.prosSendStatus.filter(item => item.status === 'error').length;
      if (successCount > 0 && errorCount === 0) {
        this.markStepComplete();
      } else if (successCount > 0 && errorCount > 0) {
        this.util.showWarning(`Message sent to ${successCount} pros. Failed for ${errorCount} pros.`);
      } else {
        this.util.showError('Failed to send messages to pros');
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    } finally {
      this.sendingInProgress = false;
    }
  }

  async markStepComplete() {
    if (!this.didCopyMessage && this.form.value.howToSend === 'copy') {
      return this.util.showError('Please copy the message first');
    }
    try {
      await this.onboardingProvider.markGoalAsCompleted('notifyProsOfChange');
      this.rightSidePanelService.setDialogPageTitle('Getting Started');
      this.rightSidePanelService.navigateTo('onboarding', {}, OnboardingComponent);
      this.onboardingProvider.setShowOnboardingInHeader(true);
      this.util.showSuccess('Onboarding step completed!');
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

}